import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { Button } from '@cotera/client/v0/components/ui/button';
import { resourceTypeIconRingClassName } from '../resources/type/resource-type-colors';
import {
  ListRowStatus,
  runStatusPresentation,
  type ListRowStatusTone,
} from '../workflow/workflow-list-row';
import { getRunsInRange, humanReadableSchedule } from './schedule-utils';
import { useScheduledRunRecords } from './use-scheduled-run-outcomes';
import {
  MAX_MATCH_TOLERANCE_MS,
  paddedHistoryWindow,
  matchToleranceMs,
  resolveOutcome,
  rollupVerdict,
  type OccurrenceOutcome,
} from './schedule-outcomes';
import type { TriggerListItem } from './triggers-list.model';

/**
 * A Monday-aligned week. The hour grid this replaced paged an arbitrary 5-day
 * stride, so an "every Monday" job landed in a different column on every page —
 * weekly cadence is the whole mental model of a schedule, and it has to hold
 * still.
 */
const WINDOW_DAYS = 7;

/**
 * Above this many occurrences of one trigger in one day, the day gets a single
 * rolled-up row instead of a row per firing. A five-minute cron fires 2,016
 * times a week; listing each would bury every other automation.
 */
const ROLLUP_THRESHOLD = 8;

/** Backstop on rendered rows after roll-ups; overflow is reported, not hidden. */
const MAX_AGENDA_ROWS = 400;

type Occurrence = {
  trigger: TriggerListItem;
  at: DateTime;
  outcome: OccurrenceOutcome;
};

type AgendaRow =
  | { type: 'occurrence'; key: string; occurrence: Occurrence }
  | {
      type: 'rollup';
      key: string;
      trigger: TriggerListItem;
      count: number;
      ran: number;
      failed: number;
      unrecorded: number;
    };

type DayGroup = {
  day: DateTime;
  rows: AgendaRow[];
  /** Index in `rows` where future occurrences start, for the "now" divider. */
  nowDividerAt: number | null;
};

/** Dot + word for an outcome, reusing the list's run vocabulary. */
function outcomePresentation(
  outcome: OccurrenceOutcome
): { tone: ListRowStatusTone; label: string } | null {
  if (outcome.kind === 'ran') {
    return runStatusPresentation(outcome.status);
  }
  if (outcome.kind === 'no-record') {
    // Deliberately not "Missed": the match is a heuristic on timestamps, so this
    // states what we can see — no run near this slot — and doesn't accuse.
    return { tone: 'idle', label: 'No run recorded' };
  }
  // 'pending' and 'upcoming' render blank: the timestamp beside them and the
  // "now" divider already say which side of the present they're on, and a column
  // of "Upcoming" on every future row is noise.
  return null;
}

/** Ochre trigger bubble, matching the same trigger's row in the list view. */
const TriggerBubble: React.FC = () => (
  <span
    className={cn(
      'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm',
      resourceTypeIconRingClassName(ResourceType.WorkflowTrigger)
    )}
  >
    <Clock className="h-4 w-4 shrink-0" strokeWidth={1.75} />
  </span>
);

const NowDivider: React.FC = () => (
  <div className="flex items-center gap-2 py-1.5" aria-hidden="true">
    <span className="h-px flex-1 bg-red-400/50" />
    <span className="text-[10px] font-medium uppercase tracking-wider text-red-500/80">
      now
    </span>
    <span className="h-px w-8 bg-red-400/50" />
  </div>
);

const OccurrenceStatus: React.FC<{ outcome: OccurrenceOutcome }> = ({
  outcome,
}) => {
  const presentation = outcomePresentation(outcome);
  if (presentation === null) {
    return null;
  }
  return <ListRowStatus tone={presentation.tone} label={presentation.label} />;
};

const RollupStatus: React.FC<{
  row: Extract<AgendaRow, { type: 'rollup' }>;
}> = ({ row }) => {
  const verdict = rollupVerdict(row);
  if (verdict.kind === 'failed') {
    return (
      <ListRowStatus
        tone="failed"
        label={`${verdict.failed} failed of ${verdict.count}`}
      />
    );
  }
  if (verdict.kind === 'ran') {
    return <ListRowStatus tone="ok" label={`${verdict.ran} ran`} />;
  }
  if (verdict.kind === 'unrecorded') {
    return <ListRowStatus tone="idle" label="No runs recorded" />;
  }
  // Upcoming or not yet knowable — the row says nothing rather than guessing.
  return null;
};

/**
 * One agenda line. Resolving the trigger per variant here is what keeps the
 * roll-up and single-occurrence shapes from having to share a field neither
 * always has.
 */
const AgendaRowView: React.FC<{
  row: AgendaRow;
  onClick: (trigger: TriggerListItem) => void;
}> = ({ row, onClick }) => {
  const trigger =
    row.type === 'occurrence' ? row.occurrence.trigger : row.trigger;
  const schedule = humanReadableSchedule(trigger.schedule);
  return (
    <button
      type="button"
      onClick={() => onClick(trigger)}
      className={cn(
        'relative isolate flex w-full min-w-0 items-center gap-3 border-b border-border/40 py-2.5 text-left last:border-b-0',
        /* The hover tint as a rounded pseudo, matching the list rows and the
           header `+` button's `rounded-sm`. On a pseudo rather than the element
           so the row's square full-width `border-b` isn't rounded with it;
           `isolate` plus `-z-10` keeps the tint behind the row's own content. */
        'before:absolute before:inset-0 before:-z-10 before:rounded-sm before:transition-colors before:content-[""]',
        'hover:before:bg-secondary/60'
      )}
    >
      <span className="w-[72px] shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {row.type === 'occurrence'
          ? row.occurrence.at.toFormat('HH:mm')
          : 'all day'}
      </span>
      <TriggerBubble />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-800">
          {trigger.name}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground/70">
          {row.type === 'rollup' ? `${row.count} runs · ${schedule}` : schedule}
        </span>
      </span>
      <span className="w-[170px] shrink-0 text-left">
        {row.type === 'rollup' ? (
          <RollupStatus row={row} />
        ) : (
          <OccurrenceStatus outcome={row.occurrence.outcome} />
        )}
      </span>
    </button>
  );
};

export type TriggersCalendarProps = {
  triggers: TriggerListItem[];
  onClickTrigger: (trigger: TriggerListItem) => void;
};

/**
 * Schedule view: an agenda of what each scheduled trigger did and will do inside
 * a week, with a per-day density strip above it.
 *
 * It replaced a 24×5 hour grid — 120 cells, ~1300px tall, for a handful of runs,
 * rendered in UTC and filtered to future occurrences only. Automation schedules
 * are sparse and repetitive, so the grid was mostly empty space you scrolled
 * past, and because it dropped the past it could never answer the question that
 * makes a schedule worth opening: did last night's job actually run?
 */
export const TriggersCalendar: React.FC<TriggersCalendarProps> = ({
  triggers,
  onClickTrigger,
}) => {
  // Local zone throughout. Cron is *interpreted* in UTC server-side (see
  // `getRunsInRange`), which is right — but the occurrences it returns are
  // absolute instants, so they render in the reader's own time.
  const [weekStart, setWeekStart] = useState(() =>
    DateTime.local().startOf('week')
  );
  const windowEnd = useMemo(
    () => weekStart.plus({ days: WINDOW_DAYS }),
    [weekStart]
  );
  const historyWindow = useMemo(
    () =>
      paddedHistoryWindow({
        fromMs: weekStart.toMillis(),
        toMs: windowEnd.toMillis(),
      }),
    [weekStart, windowEnd]
  );

  // Ticked rather than read inline: `DateTime.local()` in the render body gave a
  // new value every render, which re-ran cron expansion for every trigger each
  // time. The 30s tick is also what keeps the "now" divider honest.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const now = DateTime.fromMillis(nowMs);
  const today = now.startOf('day');

  // Only cron triggers have occurrences to plot; everything else fires on an
  // inbound signal. Counted rather than silently dropped (see the footer).
  const cronTriggers = useMemo(
    () => triggers.filter((t) => t.kind === 'cron' && t.schedule.trim() !== ''),
    [triggers]
  );
  /*
   * ...and of those, only the ones with a workflow bound. A trigger with nothing
   * attached can never produce a run, so it has no place on a timeline *of runs*:
   * its rows had nowhere to open, and every occurrence resolved to "No run
   * recorded", which reads as a run that failed rather than one that was never
   * going to happen. `workflowId` is the trigger's highest-priority binding, so
   * it is null exactly when nothing is bound.
   *
   * Omitted, not hidden — the footer says how many and why, and the list view
   * still shows them with an `Idle` status.
   */
  const cronTriggerIds = useMemo(
    () => cronTriggers.map((t) => t.id),
    [cronTriggers]
  );
  const {
    recordsByTriggerId,
    loaded,
    truncated,
    coverageStartMs,
    failed,
    runnableTriggerIds,
    bindingsLoaded,
  } = useScheduledRunRecords(
    cronTriggerIds,
    // Padded past both ends of the week: the matcher will reach up to a full
    // tolerance either side of a slot, so history clipped to the exact week left
    // the first and last slots unable to find runs that plainly happened.
    new Date(historyWindow.fromMs),
    new Date(historyWindow.toMs)
  );

  /*
   * Only triggers with an *enabled* binding. `workflowId` is not enough: a paused
   * binding still has a workflow row, so filtering on it plotted switched-off
   * schedules and then reported every past slot as "No run recorded".
   *
   * Optimistic until bindings arrive — narrowing against an empty set would blank
   * the agenda on every load. Nothing is claimed in the meantime, since `loaded`
   * stays false until run history is in and occurrences resolve to `pending`.
   */
  const scheduled = useMemo(
    () =>
      bindingsLoaded
        ? cronTriggers.filter((trigger) => runnableTriggerIds.has(trigger.id))
        : cronTriggers,
    [cronTriggers, runnableTriggerIds, bindingsLoaded]
  );
  const unattachedCount = cronTriggers.length - scheduled.length;

  const days = useMemo(
    () =>
      Array.from({ length: WINDOW_DAYS }, (_, i) =>
        weekStart.plus({ days: i })
      ),
    [weekStart]
  );

  // Expanding the crons is the expensive half and only depends on the window, so
  // it is memoized apart from outcome resolution, which re-runs on every tick and
  // on every run-history refresh.
  const plan = useMemo(() => {
    const rangeStart = weekStart.toJSDate();
    const rangeEnd = windowEnd.toJSDate();
    const slots: { trigger: TriggerListItem; at: DateTime }[] = [];
    const tolerances = new Map<string, number>();
    for (const trigger of scheduled) {
      const times = getRunsInRange(trigger.schedule, rangeStart, rangeEnd);
      // Tolerance comes from the full expansion — the cadence is a property of
      // the cron, not of how much of the week the trigger has existed for.
      tolerances.set(
        trigger.id,
        matchToleranceMs(times.map((d) => d.getTime()))
      );
      // A cron expands across the whole window regardless of when the trigger
      // was made, so a trigger created on Wednesday would otherwise be given
      // Monday and Tuesday slots — and every one of them judged "No run
      // recorded", because it did not exist yet. A schedule that did not exist
      // had no expected firing, so those slots aren't plotted at all.
      const createdMs = trigger.createdAt.getTime();
      for (const time of times) {
        if (time.getTime() < createdMs) {
          continue;
        }
        slots.push({ trigger, at: DateTime.fromJSDate(time) });
      }
    }
    slots.sort((a, b) => a.at.toMillis() - b.at.toMillis());
    return { slots, tolerances };
  }, [scheduled, weekStart, windowEnd]);

  const occurrences = useMemo(
    () =>
      plan.slots.map(({ trigger, at }) => ({
        trigger,
        at,
        outcome: resolveOutcome(
          at.toMillis(),
          nowMs,
          recordsByTriggerId.get(trigger.id) ?? [],
          plan.tolerances.get(trigger.id) ?? MAX_MATCH_TOLERANCE_MS,
          loaded,
          coverageStartMs
        ),
      })),
    [plan, recordsByTriggerId, loaded, nowMs, coverageStartMs]
  );

  /**
   * Day groups, with any trigger that fires more than `ROLLUP_THRESHOLD` times in
   * a day collapsed into one summary row for that day.
   */
  const { groups, overflow } = useMemo(() => {
    const byDay = new Map<string, Occurrence[]>();
    for (const occurrence of occurrences) {
      const key = occurrence.at.toISODate() ?? '';
      const list = byDay.get(key);
      if (list !== undefined) {
        list.push(occurrence);
      } else {
        byDay.set(key, [occurrence]);
      }
    }

    const built: DayGroup[] = [];
    let rendered = 0;
    let dropped = 0;

    for (const day of days) {
      const key = day.toISODate() ?? '';
      const dayOccurrences = byDay.get(key);
      if (dayOccurrences === undefined || dayOccurrences.length === 0) {
        continue;
      }

      const byTrigger = new Map<string, Occurrence[]>();
      for (const occurrence of dayOccurrences) {
        const list = byTrigger.get(occurrence.trigger.id);
        if (list !== undefined) {
          list.push(occurrence);
        } else {
          byTrigger.set(occurrence.trigger.id, [occurrence]);
        }
      }

      const rollups: AgendaRow[] = [];
      const single: Occurrence[] = [];
      for (const [triggerId, list] of byTrigger) {
        if (list.length <= ROLLUP_THRESHOLD) {
          single.push(...list);
          continue;
        }
        // Only positive evidence is counted. `upcoming` and `pending` are left
        // implicit — a day with none of these three is one `rollupVerdict` calls
        // unknown, which is what stops a pending day claiming "0 ran".
        let ran = 0;
        let failed = 0;
        let unrecorded = 0;
        for (const occurrence of list) {
          if (occurrence.outcome.kind === 'no-record') {
            unrecorded += 1;
          } else if (occurrence.outcome.kind === 'ran') {
            if (occurrence.outcome.status === 'failed') {
              failed += 1;
            } else {
              ran += 1;
            }
          }
        }
        rollups.push({
          type: 'rollup',
          key: `${key}-${triggerId}-rollup`,
          trigger: list[0]!.trigger,
          count: list.length,
          ran,
          failed,
          unrecorded,
        });
      }

      single.sort((a, b) => a.at.toMillis() - b.at.toMillis());
      const rows: AgendaRow[] = [
        // Frequent triggers lead the day — they're a property of the whole day
        // rather than a moment in it.
        ...rollups,
        ...single.map((occurrence) => ({
          type: 'occurrence' as const,
          key: `${occurrence.trigger.id}-${occurrence.at.toMillis()}`,
          occurrence,
        })),
      ];

      const room = MAX_AGENDA_ROWS - rendered;
      if (room <= 0) {
        dropped += rows.length;
        continue;
      }
      const kept = rows.slice(0, room);
      dropped += rows.length - kept.length;
      rendered += kept.length;

      let nowDividerAt: number | null = null;
      if (day.hasSame(today, 'day')) {
        const firstFuture = kept.findIndex(
          (row) =>
            row.type === 'occurrence' && row.occurrence.at.toMillis() > nowMs
        );
        nowDividerAt = firstFuture === -1 ? kept.length : firstFuture;
      }

      built.push({ day, rows: kept, nowDividerAt });
    }

    return { groups: built, overflow: dropped };
  }, [days, occurrences, today, nowMs]);

  const goToPrev = useCallback(
    () => setWeekStart((d) => d.minus({ days: WINDOW_DAYS })),
    []
  );
  const goToNext = useCallback(
    () => setWeekStart((d) => d.plus({ days: WINDOW_DAYS })),
    []
  );
  const goToThisWeek = useCallback(
    () => setWeekStart(DateTime.local().startOf('week')),
    []
  );

  const rangeLabel = `${weekStart.toFormat('d MMM')} – ${windowEnd
    .minus({ days: 1 })
    .toFormat('d MMM')}`;

  /**
   * Which week you're on, relative to now. Dates alone can't say this — you'd
   * have to already know today's date to read "18 Nov – 24 Nov" as the current
   * week — and it's the state the "This week" button was being mistaken for.
   */
  const currentWeekStart = now.startOf('week');
  const weeksFromNow = Math.round(
    weekStart.diff(currentWeekStart, 'weeks').weeks
  );
  const isCurrentWeek = weeksFromNow === 0;
  const relativeLabel =
    weeksFromNow === 0
      ? 'This week'
      : weeksFromNow === 1
      ? 'Next week'
      : weeksFromNow === -1
      ? 'Last week'
      : weeksFromNow > 1
      ? `In ${weeksFromNow} weeks`
      : `${Math.abs(weeksFromNow)} weeks ago`;

  const footerNotes = [
    overflow > 0 ? `${overflow} further runs not shown.` : null,
    unattachedCount > 0
      ? `${unattachedCount} scheduled trigger${
          unattachedCount === 1 ? ' has' : 's have'
        } no enabled workflows attached, so nothing runs at those times.`
      : null,
    failed
      ? "Run history couldn't be loaded, so these slots don't show what happened."
      : null,
    truncated
      ? "Older runs in this window weren't loaded, so the earliest slots don't show what happened."
      : null,
  ].filter((part): part is string => part !== null);

  return (
    // No horizontal padding of its own: as a `contentOverride` this renders
    // inside the list container, where the page frame's gutters already apply.
    // Its own `px-8` used to stack on those, indenting the calendar 32px
    // further than the rows it stands in for.
    <div className="flex flex-col pb-10 pt-2">
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="min-w-0">
          <p className="text-base font-semibold text-slate-800">{rangeLabel}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {/* Timezone named once, up front. The old grid printed "UTC" in a
                10px corner cell and left every reader doing the offset in their
                head. */}
            {`${relativeLabel} · Times in ${now.toFormat('ZZZZ')}`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrev}
            aria-label="Previous week"
            title="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {/*
           * "Today" rather than "This week": it's the label every calendar uses
           * for return-to-now, including in week view, and this is a button people
           * press without reading. The week containing today is what you land on.
           *
           * An action, not a state — so it goes dead once it has nothing to do,
           * which is also the clearest signal that you're already home. Disabled
           * rather than hidden, or the arrows would shift under the cursor as you
           * page.
           */}
          <Button
            variant="outline"
            size="sm"
            onClick={goToThisWeek}
            disabled={isCurrentWeek}
            title="Jump to the current week"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNext}
            aria-label="Next week"
            title="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="flex min-h-[35dvh] items-center justify-center py-10">
          <div className="flex max-w-md flex-col items-center rounded-xl border border-dashed border-border px-8 py-12 text-center">
            <span
              className={cn(
                'mb-5 inline-flex h-12 w-12 items-center justify-center rounded-md',
                resourceTypeIconRingClassName(ResourceType.WorkflowTrigger)
              )}
            >
              <Clock className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <p className="text-base font-semibold text-slate-800">
              Nothing scheduled this week
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {scheduled.length > 0
                ? 'These schedules have no runs inside this week — try another week.'
                : unattachedCount > 0
                ? 'The scheduled triggers here have no enabled workflows attached, so there is nothing for them to run.'
                : 'None of these automations run on a schedule.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          {groups.map((group) => {
            // Today is the only group that mixes past with future — it's the one
            // holding the "now" divider — so it's the row the eye should land on.
            // Emphasis comes from a filled pill: `primary` is near-black in this
            // theme rather than an accent hue, so coloured *text* would be
            // indistinguishable from the other days' labels. It's the treatment
            // the hour grid used for its today circle.
            const isToday = group.day.hasSame(today, 'day');
            return (
              <div key={group.day.toISODate()} className="mb-5 last:mb-0">
                <div
                  className={cn(
                    'flex items-center gap-2 border-b py-2',
                    isToday ? 'border-primary/30' : 'border-border/60'
                  )}
                >
                  <span
                    className={cn(
                      'text-[11px] uppercase tracking-wider',
                      isToday
                        ? 'rounded-full bg-primary px-2 py-0.5 font-semibold text-primary-foreground'
                        : 'font-medium text-muted-foreground'
                    )}
                  >
                    {isToday ? 'Today' : group.day.toFormat('cccc')}
                  </span>
                  <span
                    className={cn(
                      'text-xs',
                      isToday
                        ? 'font-medium text-primary'
                        : 'text-muted-foreground/60'
                    )}
                  >
                    {group.day.toFormat('d MMM')}
                  </span>
                </div>
                {group.rows.map((row, index) => (
                  <React.Fragment key={row.key}>
                    {group.nowDividerAt === index ? <NowDivider /> : null}
                    <AgendaRowView row={row} onClick={onClickTrigger} />
                  </React.Fragment>
                ))}
                {group.nowDividerAt === group.rows.length ? (
                  <NowDivider />
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Omissions this view can't show any other way: schedules with nothing
          attached, run history clipped by the row cap, rows past the render cap.
          Event-driven triggers are *not* listed here — a clock view having no
          clock entry for them needs no explaining. */}
      {footerNotes.length > 0 ? (
        <p className="mt-6 text-xs text-muted-foreground/70">
          {footerNotes.join(' ')}
        </p>
      ) : null}
    </div>
  );
};
